"use client"

import { useState, useEffect } from "react"
import { fetchStudentData, verifyStudent, deleteStudent } from "../comman/Student.js"
import Sidebar from "../components/Sidebar.js"
import { FaEdit, FaTrash, FaCheckCircle } from "react-icons/fa"
import AddStudentModal from "../components/AddStudentModal.js"
import StudentProfileModal from "../components/StudentProfileModal.js"
import NotificationModal from "../components/Notifications.js"
import { IoMdNotificationsOutline, IoMdSearch } from "react-icons/io"
import { IoPersonAdd, IoEye } from "react-icons/io5"

const StudentDetails = () => {
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [studentToNotification, setStudentToNotification] = useState(null)
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)
  const [isModal, setIsModal] = useState(false)
  const [studentToEdit, setStudentToEdit] = useState(null)

  const handleVerify = async (index, studentId, currentVerifyStatus) => {
    if (currentVerifyStatus === true) {
      return
    }
    try {
      await verifyStudent(studentId)
      const updatedStudents = [...students]
      updatedStudents[index].verify = true
      setStudents(updatedStudents)
    } catch (error) {
      console.error("Error verifying student:", error)
      alert("Error verifying student")
    }
  }

  const handleViewProfile = (student) => {
    setStudentToEdit(student)
    setIsModal(true)
  }

  const handleEdit = (student) => {
    setStudentToEdit(student)
    setIsModalOpen(true)
  }

  const handleNotification = (student) => {
    setStudentToNotification(student)
    setIsNotificationModalOpen(true)
  }

  const handleDelete = async (studentId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this student?")
    if (confirmDelete) {
      try {
        await deleteStudent(studentId)
        const updatedStudents = students.filter((student) => student._id !== studentId)
        setStudents(updatedStudents)
        alert("Student deleted successfully!")
      } catch (error) {
        console.error("Error deleting student:", error)
        alert("Failed to delete student")
      }
    }
  }

  const handleAddStudent = () => {
    fetchData()
    setIsModalOpen(false)
  }

  const handleUpdateStudent = () => {
    fetchData()
    setIsModalOpen(false)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setIsModal(false)
    setIsNotificationModalOpen(false)
    setStudentToNotification(null)
    setStudentToEdit(null)
  }

  const fetchData = async () => {
    try {
      const data = await fetchStudentData()
      setStudents(data)
      setLoading(false)
    } catch (error) {
      setError("Error fetching student data")
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const filteredStudents = students.filter(
    (student) =>
      student.profile?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64 overflow-hidden">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#12143F]">Student Management</h1>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsNotificationModalOpen(true)}
              className="flex items-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
            >
              <IoMdNotificationsOutline size={20} className="mr-2" />
              <span className="hidden sm:inline">Notify All</span>
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
            >
              <IoPersonAdd size={18} className="mr-2" />
              <span className="hidden sm:inline">Add Student</span>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <div className="mb-6">
            <div className="relative w-full md:w-96">
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <IoMdSearch className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[calc(100vh-250px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-white uppercase bg-[#12143F] sticky top-0">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Phone No
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Academy Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, index) => (
                  <tr
                    key={student._id}
                    className={`border-b ${index % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-gray-100`}
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">{student.profile?.name}</td>
                    <td className="px-6 py-4">{student.email}</td>
                    <td className="px-6 py-4">{student.profile?.phone}</td>
                    <td className="px-6 py-4">{student.academyId?.map((academy) => academy.name).join(", ") || "-"}</td>
                    <td className="px-6 py-4">
                      {student.verify ? (
                        <div className="flex items-center text-green-600">
                          <FaCheckCircle className="w-4 h-4 mr-1.5" />
                          <span className="font-medium">Verified</span>
                        </div>
                      ) : (
                        <button
                          className="px-4 py-1.5 bg-[#12143F] text-white rounded-md transition-all hover:bg-opacity-90 text-sm font-medium"
                          onClick={() => handleVerify(index, student._id, student.verify)}
                        >
                          Verify Now
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleEdit(student)}
                          className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200"
                          title="Edit"
                        >
                          <FaEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleViewProfile(student)}
                          className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200"
                          title="View Profile"
                        >
                          <IoEye size={18} />
                        </button>
                        <button
                          onClick={() => handleNotification(student)}
                          className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200"
                          title="Send Notification"
                        >
                          <IoMdNotificationsOutline size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(student._id)}
                          className="text-red-500 hover:text-red-700 transition-colors duration-200"
                          title="Delete"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddStudentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        studentToEdit={studentToEdit}
        onAddStudent={handleAddStudent}
        onUpdateStudent={handleUpdateStudent}
      />
      <StudentProfileModal isOpen={isModal} onClose={handleCloseModal} student={studentToEdit} />
      <NotificationModal isOpen={isNotificationModalOpen} onClose={handleCloseModal} student={studentToNotification} />
    </div>
  )
}

export default StudentDetails

